import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from './_services/api.service';
import { UserService } from './_services/user.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from './_services/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from './_constants/constants';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { CurrencyStoreService } from './_services/currency-store.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {

    constructor(public apiService: ApiService,
        private authService: AuthenticationService,
        private router: Router,
        private translateService: TranslateService,
        public userService: UserService,
        private serviceWorker: SwUpdate,
        private toastr: ToastrService,
        private currencyRates: CurrencyStoreService,
        @Inject(PLATFORM_ID) private platformId: Object) {
            this.translateService.setDefaultLang(AppConstants.FRENCH);
    }

    ngOnInit(): void {
        // Browser-only initialization
        if (isPlatformBrowser(this.platformId)) {
            // Always default to French on page load
            // this.translateService.use(AppConstants.FRENCH).subscribe(() => {
            //     localStorage.removeItem(AppConstants.CURRENT_LANG_STORE_KEY);
            // });

// Set language: stored preference > browser language > French fallback
            const storedLang = localStorage.getItem(AppConstants.CURRENT_LANG_STORE_KEY);
            if (storedLang) {
                this.translateService.setDefaultLang(storedLang);
            } else {
                const browserLang = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
                const detectedLang = browserLang.startsWith('en') ? AppConstants.ENGLISH : AppConstants.FRENCH;
                this.translateService.setDefaultLang(detectedLang);
                localStorage.setItem(AppConstants.CURRENT_LANG_STORE_KEY, detectedLang);
            }

            // Check for payment success message
            const paymentSuccess = sessionStorage.getItem('payment_success');
            if (paymentSuccess) {
                this.toastr.success(paymentSuccess);
                sessionStorage.removeItem('payment_success');
            }

            // Force-dismiss toasts on click anywhere in the toast area
            document.addEventListener('click', (e: any) => {
                const toast = e.target.closest('.ngx-toastr');
                if (!toast) return;

                // Try the native close button first
                const closeBtn = toast.querySelector('.toast-close-button') as HTMLElement;
                if (closeBtn) {
                    closeBtn.click();
                }

                // Fallback: remove from DOM if still visible after click
                setTimeout(() => {
                    if (document.body.contains(toast)) {
                        toast.remove();
                    }
                }, 200);
            });
        }

        // clear cached currencies and save new rate for default currency
        this.currencyRates.clear();
        const defaultCurrency = environment.currency;
        this.currencyRates.add({from: defaultCurrency, to: defaultCurrency, rate: 1});

        this.router.events.subscribe((evt) => {
            if (!(evt instanceof NavigationEnd)) {
                return;
            }

            // Browser-only: close nav sidebar and scroll to top
            if (isPlatformBrowser(this.platformId)) {
                const navEl = document.getElementById('mySidenav');
                if (navEl) {
                    navEl.style.width = '0';
                }
                window.scrollTo(0, 0);
            }
        });

        if (isPlatformBrowser(this.platformId) && this.serviceWorker.isEnabled) {
            this.serviceWorker.versionUpdates.pipe(
                filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
                switchMap(() => this.translateService.get('common.updates_available'))
            ).subscribe((message) => { // should user confirm to get changes now, changes should immediately reflect
                location.reload();
            });
        }
    }
}
